/**
 * budget-derived-routes — the SCHEMA-FREE, behaviour-free ROUTE-ONLY half of the
 * budget derived tail (#1392 S2 / #1600; the #904 `hb-derived-routes` recipe).
 *
 * The FULL `budgetDerivedPlugin` (`./budget-derived-plugin.js`) composes its
 * behavioural entities (a `store` + SIX `query`s) alongside its `component`/
 * `route` pair. The studio ENTRY's router needs only the ROUTE TABLE + the
 * route's (lazy) editor — never a `store`/`query` entity — so composing the full
 * plugin on the eager entry would drag those entity literals, the `$expr`
 * sources, and (through them) `@ubiclouds/v5-genome-gen/sample` onto the entry
 * for no boot benefit.
 *
 * So the eager `genome.ts` composes THIS, and the lazy `genome-full.ts` composes
 * the FULL plugin (whose 16-kind graph the agent catalog / PERCEIVE /
 * genome-graph / census consume). The id + path + the route entities + the lazy
 * editor map live HERE, the entry-safe home; the full plugin IMPORTS them and
 * prepends its behavioural block, so its entity array stays element-identical to
 * a single hand-written list.
 *
 * NOTHING here imports `./budget-derived-expr.js`, `./budget-derived-plugin.js`
 * or `@ubiclouds/v5-sdk/runtime` — that separation is what keeps
 * `createQueryRuntime` off the eager entry (gate 8, measured not assumed).
 *
 * @module @ubiclouds/v5-studio/genome/budget-derived-routes
 */

import { lazy, type ComponentType } from 'react';
import type { ComposableGenomePlugin } from '@ubiclouds/v5-sdk/plugins';
import type { EntityDef } from '@ubiclouds/v5-sdk/genome';

/** The bespoke derived-tail plugin id (its entity namespace). */
export const BUDGET_DERIVED_PLUGIN_ID = 'budget-derived';

/**
 * The derived-summary route path. It sits STRICTLY under the generated
 * `/budget` prefix, so `discoverIndexSiblings` auto-lists it under the generated
 * index's "More" group with ZERO `genome-gen` change (the `/monzo/insights` and
 * `/home-battery/configurator` precedent).
 */
export const BUDGET_SUMMARY_PATH = '/budget/summary';

/**
 * The `/budget/credentials` route path (#1602). Like the summary above it sits
 * STRICTLY under the generated `/budget` prefix, so `discoverIndexSiblings`
 * auto-lists it under the generated index's "More" group with ZERO `genome-gen`
 * change.
 */
export const BUDGET_CREDENTIALS_PATH = '/budget/credentials';

/**
 * The `/budget/actuals` route path (#1601). A SEPARATE route rather than a
 * section on the summary: the summary is a BASELINE view with no month, no data
 * source and no async state, while actuals needs all three plus loading and
 * degraded — folding them would grow the summary's e2e, chunk and empty-state
 * matrix for a reading the operator asks for separately. It sits STRICTLY under
 * the generated `/budget` prefix, so `discoverIndexSiblings` auto-lists it under
 * the index's "More" group with ZERO `genome-gen` change.
 */
export const BUDGET_ACTUALS_PATH = '/budget/actuals';

/**
 * The `/budget/alerts` route path (#1603) — where an alert LANDS.
 *
 * **NOT a push notification and NOT a toast, and both refusals are deliberate.**
 * NOTIFY's live browser push (#114 Phase-C) is mock-first with delivery explicitly
 * deferred and deploy/infra-gated, so designing against it would ship a feature
 * that silently delivers nothing. A toast is worse in a subtler way: the chrome
 * family draws the line itself — a toast *appears, stacks and auto-dismisses* —
 * and **an alert derived from data must not evaporate.** A persistent route plus
 * a count Banner on `/budget/actuals` are surfaces that actually deliver.
 *
 * Like its three siblings it sits STRICTLY under the generated `/budget` prefix,
 * so `discoverIndexSiblings` auto-lists it under the index's "More" group with
 * ZERO `genome-gen` change.
 */
export const BUDGET_ALERTS_PATH = '/budget/alerts';

/** The editor key `resolveRoutes` looks up (the segment after `component/`). */
export const BUDGET_SUMMARY_EDITOR_KEY = 'budget-summary';

/** The credentials editor key. */
export const BUDGET_CREDENTIALS_EDITOR_KEY = 'budget-credentials';

/** The actuals editor key (#1601). */
export const BUDGET_ACTUALS_EDITOR_KEY = 'budget-actuals';

/** The alerts editor key (#1603). */
export const BUDGET_ALERTS_EDITOR_KEY = 'budget-alerts';

/** The fully-qualified component id the route resolves to. */
export const BUDGET_SUMMARY_COMPONENT = `${BUDGET_DERIVED_PLUGIN_ID}:component/${BUDGET_SUMMARY_EDITOR_KEY}`;

/** The fully-qualified credentials component id (#1602). */
export const BUDGET_CREDENTIALS_COMPONENT = `${BUDGET_DERIVED_PLUGIN_ID}:component/${BUDGET_CREDENTIALS_EDITOR_KEY}`;

/** The fully-qualified actuals component id (#1601). */
export const BUDGET_ACTUALS_COMPONENT = `${BUDGET_DERIVED_PLUGIN_ID}:component/${BUDGET_ACTUALS_EDITOR_KEY}`;

/** The fully-qualified alerts component id (#1603). */
export const BUDGET_ALERTS_COMPONENT = `${BUDGET_DERIVED_PLUGIN_ID}:component/${BUDGET_ALERTS_EDITOR_KEY}`;

/** The split-chunk summary editor — the view AND the query runtime stay off the entry. */
const LazyBudgetSummary: ComponentType = lazy(() => import('./BudgetSummary.js'));

/**
 * The split-chunk credentials editor (#1602). This `lazy` is what keeps the whole
 * `@ubiclouds/v5-vault` transport — the secrets admin schemas, the live fetcher,
 * the reveal hook — OFF the eager entry closure, which is measured on both gate-8
 * axes rather than asserted. It is deliberately a SECOND route on the EXISTING
 * plugin rather than a second plugin: a new plugin would bump the adopter counts
 * pinned in `plugin-contract-declaration.test.ts`, and a numeric pin is not a
 * union-mergeable line.
 */
const LazyBudgetCredentials: ComponentType = lazy(() => import('./BudgetCredentials.js'));

/**
 * The split-chunk actuals editor (#1601). This `lazy` is what keeps the query
 * runtime, the assignment matcher AND the `@ubiclouds/v5-finance` live-feed
 * transport OFF the eager entry closure — measured on both gate-8 axes rather
 * than asserted. A THIRD route on the EXISTING plugin, deliberately not a second
 * plugin: a new plugin bumps four numeric adopter pins, and a numeric pin is not
 * a union-mergeable line.
 */
const LazyBudgetActuals: ComponentType = lazy(() => import('./BudgetActuals.js'));

/**
 * The split-chunk alerts editor (#1603). This `lazy` is what keeps the query
 * runtime, the effect-FSM bridge and the acknowledgement store OFF the eager entry
 * closure — measured on both gate-8 axes rather than asserted. A FOURTH route on
 * the EXISTING plugin, deliberately not a second plugin: a new plugin bumps four
 * numeric adopter pins, and a union-merge of two numeric bumps is silently wrong.
 */
const LazyBudgetAlerts: ComponentType = lazy(() => import('./BudgetAlerts.js'));

/** The `component`/`route` entities the derived tail contributes (keyed by `meta.id`). */
export const BUDGET_DERIVED_ROUTE_ENTITIES: readonly EntityDef[] = [
  { kind: 'component', meta: { id: BUDGET_SUMMARY_COMPONENT } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: BUDGET_SUMMARY_COMPONENT }],
    meta: {
      id: `${BUDGET_DERIVED_PLUGIN_ID}:route/summary`,
      path: BUDGET_SUMMARY_PATH,
      title: 'Budget Summary',
    },
  },
  { kind: 'component', meta: { id: BUDGET_CREDENTIALS_COMPONENT } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: BUDGET_CREDENTIALS_COMPONENT }],
    meta: {
      id: `${BUDGET_DERIVED_PLUGIN_ID}:route/credentials`,
      path: BUDGET_CREDENTIALS_PATH,
      title: 'Budget Credentials',
    },
  },
  { kind: 'component', meta: { id: BUDGET_ACTUALS_COMPONENT } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: BUDGET_ACTUALS_COMPONENT }],
    meta: {
      id: `${BUDGET_DERIVED_PLUGIN_ID}:route/actuals`,
      path: BUDGET_ACTUALS_PATH,
      title: 'Budget Actuals',
    },
  },
  { kind: 'component', meta: { id: BUDGET_ALERTS_COMPONENT } },
  {
    kind: 'route',
    refs: [{ rel: 'routes-to', target: BUDGET_ALERTS_COMPONENT }],
    meta: {
      id: `${BUDGET_DERIVED_PLUGIN_ID}:route/alerts`,
      path: BUDGET_ALERTS_PATH,
      title: 'Budget Alerts',
    },
  },
];

/** The lazy editor map — SHARED by the route-only plugin and the FULL plugin. */
export const BUDGET_DERIVED_EDITORS: Readonly<Record<string, ComponentType>> = Object.freeze({
  [BUDGET_SUMMARY_EDITOR_KEY]: LazyBudgetSummary,
  [BUDGET_CREDENTIALS_EDITOR_KEY]: LazyBudgetCredentials,
  [BUDGET_ACTUALS_EDITOR_KEY]: LazyBudgetActuals,
  [BUDGET_ALERTS_EDITOR_KEY]: LazyBudgetAlerts,
});

/**
 * The ROUTE-ONLY budget derived-tail plugin (composed on the studio ENTRY by
 * `genome.ts`). Same `editors` as the FULL plugin; `entities()` are the
 * `component`/`route` pair ONLY.
 */
export const budgetDerivedRoutePlugin: ComposableGenomePlugin = Object.freeze({
  id: BUDGET_DERIVED_PLUGIN_ID,
  name: 'Budget Summary',
  // NO `version` / `trustTier`. #1176's ledger measures both as DEAD-DECLARATION —
  // nothing in the tree READS either — and it flags a rising adopter count as
  // DEAD-GROWING: "plugins are being made to declare into a void". They ratchet
  // DOWN only, so a NEW plugin is the one place the count can be held flat for
  // free. The older sibling tails still write them; this one does not.
  editors: BUDGET_DERIVED_EDITORS,
  entities(): EntityDef[] {
    return [...BUDGET_DERIVED_ROUTE_ENTITIES];
  },
});
